import { useQuery as useApolloQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { Contact } from '../../../types/contact.types';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT } from '../../../lib/graphql/queries/contacts';
import { getEAlertStatusSummary } from '../../../utils/ealertStatus';
import type { CrmStageProjection } from '../../../types/crmStage.types';

// ── Nested list item types ──────────────────────────────────────────

export interface GQLCallItem {
  callId: string;
  conversationId: string | null;
  direction: string;
  inbound: boolean | null;
  fromNumber: string | null;
  toNumber: string | null;
  status: string;
  outcome: string | null;
  answeredBy: string | null;
  durationSeconds: number | null;
  initiatedAt: string | null;
  endedAt: string | null;
  summary: string | null;
  transcript: string | null;
  sentiment: string | null;
  recordingUrl: string | null;
  agentId: string | null;
  flowName: string | null;
  flowType: string | null;
  callAnalysisTimeline: string | null; // JSON string: [{event, timestamp}]
  callAnalysisAnswers: string | null;  // JSON string: {key: value}
  isTest: boolean | null;
  createdAt: string;
  transfer?: {
    transferId?: string;
    status?: string;
    transferReason?: string;
    targetAgentNumber?: string;
    failureReason?: string;
    recordingUrl?: string;
    voicemailUrl?: string;
    answered?: boolean | null;
    acceptedBy?: string | null;
  } | null;
}

export interface GQLMessageItem {
  messageId: string;
  conversationId: string;
  channel: string;
  direction: string;
  fromAddress: string;
  toAddress: string;
  subject: string;
  body: string;
  bodyHtml: string | null;
  status: string;
  eventType: string | null;
  templateName: string | null;
  campaignId: string | null;
  campaignStatus: string | null;
  scheduledTime: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  createdAt: string;
  sentAt: string | null;
  /** AGENTIC for Remi conversations, LEGACY for eAlert/flow-template sends; null off email. */
  emailKind?: 'AGENTIC' | 'LEGACY' | null;
  /** What the sender wrote, without the history their client quoted (from the API). */
  replyText?: string | null;
  /** On a Remi conversation row of `latestPerThread` lists: its email count. */
  threadMessageCount?: number | null;
  /** The quoted history below the reply; empty when none (from the API). */
  quotedText?: string | null;
  contactId?: string | null;
}

export interface GQLNoteItem {
  noteId: string;
  subject: string;
  body: string;
  authorId: string;
  authorName: string;
  authorType: string;
  noteType: string;
  isPinned: boolean;
  category: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface GQLSavedPropertyItem {
  propertyId: string;
  mlsNumber: string;
  mlsBoardCode: string | null;
  listingStatus: string | null;
  price: number | null;
  listPrice: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  address: string | null;
  listingOffice: string | null;
  primaryPhotoUrl: string | null;
  features: string | null;
  isActive: boolean | null;
  savedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number | null;
  createdAt: string | null;
}

export interface GQLValuationItem {
  valuationId: string;
  fullAddress: string | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  estimatedValue: number | null;
  valueLow: number | null;
  valueHigh: number | null;
  confidenceScore: number | null;
  valuationSource: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  lotSize: number | null;
  yearBuilt: number | null;
  propertyType: string | null;
  lookupCount: number | null;
  isHotLead: boolean | null;
  firstLookedUpAt: string | null;
  lastLookedUpAt: string | null;
  createdAt: string | null;
}

export interface GQLWebActivityItem {
  activityId: string;
  eventType: string;
  eventCategory: string | null;
  pageUrl: string | null;
  deviceType: string | null;
  properties: string | null;
  sessionId: string | null;
  isGuest: boolean;
  guestInfo: string | null;
  timestamp: string | null;
}

export interface GQLActivityFeedItem {
  eventId: string;
  threadMessageCount?: number | null;
  eventType: string;
  eventSubtype: string | null;
  channel: string | null;
  summary: string | null;
  outcome: string | null;
  sentiment: string | null;
  durationSeconds: number | null;
  callId: string | null;
  messageId: string | null;
  sourceSystem: string | null;
  metadata: string | null;
  eventAt: string;
  createdAt: string | null;
}

export interface GQLPropertyEnrichmentItem {
  enrichmentId: string;
  enrichmentStatus: string | null;
  dataSource: string | null;
  propertyAddress: string | null;
  processedData: string | null; // JSON string
  rawApiResponse: string | null; // JSON string — full RealEstateAPI payload
  confidenceScore: number | null;
  lastEnrichmentAt: string | null;
  updatedAt: string | null;
}

export interface GQLAgentSummary {
  agentId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  photoUrl: string;
}

// Generic paginated list wrapper
interface PaginatedList<T> {
  items: T[];
  total: number;
  hasMore: boolean;
}

// ── SPEC-1708: suggested contact status ─────────────────────────────
export type GQLSuggestedStatusValue =
  | 'HANDOFF'
  | 'APPOINTMENT_SET'
  | 'NURTURING'
  | 'DNC'
  | 'UNKNOWN';

export interface GQLSuggestedStatus {
  status: GQLSuggestedStatusValue;
  sourceField: string | null;
  sourceValue: string | null;
}

// ── SPEC-1712: per-channel consent ──────────────────────────────────
export type GQLConsentState = 'UNKNOWN' | 'OPTED_IN' | 'OPTED_OUT' | 'DNC';

export interface GQLChannelConsent {
  channel: string; // 'sms' | 'email' | 'call'
  state: GQLConsentState;
  sourceField: string | null;
  recordedAt: string | null;
  description: string | null;
  /** Last time this channel was opted in, whatever the current state. Selected
   *  by the Consent tab and by GET_CONTACT, so the tab and the Lead Info rail
   *  date a channel the same way. Absent on the contacts LIST query, which
   *  would pay one ledger read per row for it. */
  lastConsentedAt?: string | null;
  /** Latest ledger entry for this channel, same shape as a history row. */
  lastEvent?: {
    consentEventId: string;
    channel: string;
    eventType: string;
    source: string | null;
    consentState: boolean | null;
    actor: string | null;
    reason: string | null;
    occurredAt: string | null;
    createdAt: string | null;
  } | null;
}

export interface GQLContactConsent {
  sms: GQLChannelConsent;
  email: GQLChannelConsent;
  call: GQLChannelConsent;
}

// ── SPEC-1709: AI intelligence ──────────────────────────────────────
export interface GQLAiInsight {
  insightType: string | null;
  insightKey: string | null;
  description: string | null;
  updatedAt: string | null;
}

export interface GQLAiPreferences {
  budgetMin: number | null;
  budgetMax: number | null;
  propertyTypes: string[];
  locationsOfInterest: string[];
  bedroomsMin: number | null;
  bedroomsMax: number | null;
  bathroomsMin: number | null;
  sqftMin: number | null;
  sqftMax: number | null;
  mustHaveFeatures: string[];
  niceToHaveFeatures: string[];
  timeline: string | null;
  intentLevel: string | null;
}

export interface GQLAiNextTouch {
  channel: string | null;
  scheduledAt: string | null;
  reason: string | null;
}

export interface GQLContactAiIntelligence {
  summary: string | null;
  whatRemiKnows: string[];
  preferences: GQLAiPreferences | null;
  objections: GQLAiInsight[];
  openCommitments: GQLAiInsight[];
  nextScheduledTouch: GQLAiNextTouch | null;
}

// ── SPEC-1710: Flywheel follow-up plan ──────────────────────────────
export interface GQLFollowUpPlanStep {
  stepId: string;
  channel: string | null; // sms | call | email | wait | condition | action
  when: string | null;
  title: string | null;
  purpose: string | null;
  status: string | null;
  source: string | null; // persisted | synthesized | ealert
  ealertInterval: string | null; // daily | weekly | bi-weekly (ealert steps only)
}

export interface GQLFollowUpPlan {
  steps: GQLFollowUpPlanStep[];
  hasActiveFlow: boolean;
  // SPEC-2023: the contact's last recorded call outcome, surfaced only when the
  // plan has no upcoming steps. Used purely as a "what happened last" label in
  // the Future Outreach Plan card — these never coexist with upcoming steps and
  // do NOT imply a handoff. All three are populated (or null) together.
  resolvedOutcome: string | null; // last Calls.outcome, e.g. "conversation"
  resolvedStrategyId: string | null; // strategy id of the resolved flow
  resolvedOutcomeAt: string | null; // ISO 8601 — when the outcome was recorded
}

// ── Root Contact detail shape from GraphQL ──────────────────────────

interface GQLContactDetail {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneStatus: string;
  emailStatus: string;
  address: { street: string; city: string; state: string; zip: string } | null;
  leadSource: string;
  leadSourceSecondary: string;
  leadScore: number;
  intentLevel: string;
  leadIntent: string | null;
  engagementScore: number;
  status: string;
  lifecycleStage: string;
  recentOutcome: string;
  lastOutcomeChannel: string;
  outcomeHistory: string | null;
  assignedAgentId: string;
  assignedPondId: string | null;
  claimedLead: string;
  claimedLeadSource: string | null;
  crmSource: string;
  crmId: string;
  crmSynced: boolean;
  crmStages: CrmStageProjection[];
  crmSyncReason: string | null;
  crmCreated: string | null;
  crmLastUpdated: string | null;
  crmSyncedAt: string | null;
  lastSyncDirection: string | null;
  lastSyncTimestamp: string | null;
  lastSyncedAt: string | null;
  cadencePath: string | null;
  activeFlows: string | null;
  finishedFlows: string | null;
  activeFlowId: string | null;
  flywheelEnrolled: boolean;
  flywheelOptOut: boolean;
  flywheelOptOutAt: string | null;
  optIn: boolean;
  optInDate: string | null;
  optInDesc: string | null;
  callTester: boolean;
  lastCallAnswered: string | null;
  hasEalerts: boolean;
  valuationRequested: boolean;
  websiteActivitySource: string | null;
  activeHandoffId: string | null;
  contactHealth: number | null;
  readinessScore: number | null;
  readinessBand: string | null;
  aiPaused: boolean;
  aiPausedAt: string | null;
  aiPausedReason: string;
  aiAssistantId: string | null;
  aiThreadId: string | null;
  lastMessageAt: string | null;
  lastMessageFrom: string | null;
  tags: string | string[];
  customFields: string;
  metadata: string;
  createdAt?: string;
  updatedAt?: string;
  assignedAgent: GQLAgentSummary | null;
  notes: PaginatedList<GQLNoteItem>;
  propertyEnrichment: { items: GQLPropertyEnrichmentItem[]; total: number } | null;
  suggestedStatus: GQLSuggestedStatus | null;
  consent: GQLContactConsent | null;
  aiIntelligence: GQLContactAiIntelligence | null;
  followUpPlan: GQLFollowUpPlan | null;
}

// Response shape from GET_CONTACT query
interface GetContactData {
  contact: GQLContactDetail | null;
}

// ── Type returned by the hook ───────────────────────────────────────

export type MappedContact = Contact & {
  notes: PaginatedList<GQLNoteItem>;
  assignedAgent: GQLAgentSummary | null;
  propertyEnrichment: { items: GQLPropertyEnrichmentItem[]; total: number } | null;
  // Extra fields not on Contact type but available from GraphQL
  outcomeHistory: string | null;
  crmSyncReason: string | null;
  crmCreated: string | null;
  crmLastUpdated: string | null;
  crmSyncedAt: string | null;
  lastSyncDirection: string | null;
  cadencePath: string | null;
  activeFlows: string | null;
  finishedFlows: string | null;
  activeFlowId: string | null;
  aiAssistantId: string | null;
  aiThreadId: string | null;
  lastMessageAt: string | null;
  lastMessageFrom: string | null;
  lastSyncTimestamp: string | null;
  lastSyncedAt: string | null;
  // SPEC-1708/1709/1710/1712 — Remi intelligence fields
  suggestedStatus: GQLSuggestedStatus | null;
  consent: GQLContactConsent | null;
  aiIntelligence: GQLContactAiIntelligence | null;
  followUpPlan: GQLFollowUpPlan | null;
};

// ── Helpers ─────────────────────────────────────────────────────────

function parseTags(tags: unknown): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return tags.split(',').map((t) => t.trim()).filter(Boolean);
    }
  }
  return [];
}

/** Map GraphQL contact to the Contact shape the UI expects */
function mapGQLContactDetail(c: GQLContactDetail): MappedContact {
  const agent = c.assignedAgent;
  const ealertStatus = getEAlertStatusSummary({
    has_ealerts_registered: c.hasEalerts ?? false,
    metadata: c.metadata,
    customFields: c.customFields,
  });

  return {
    // Core identity
    id: c.contactId,
    organization_id: '', // filled by caller if needed
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    phoneNumber: c.phone ?? '',
    phoneStatus: c.phoneStatus ?? '',
    email: c.email ?? '',
    email_status: c.emailStatus ?? undefined,
    read: true,

    // Address
    address: c.address ?? { street: '', city: '', state: '', zip: '' },

    // Lead info
    leadSource: c.leadSource ?? undefined,
    leadSourceSecondary: c.leadSourceSecondary ?? undefined,
    leadIntent: c.leadIntent ?? c.intentLevel ?? undefined,
    score: c.leadScore ?? undefined,
    engagementScore: c.engagementScore ?? undefined,
    recentOutcome: c.recentOutcome ?? undefined,
    lastOutcomeChannel: c.lastOutcomeChannel ?? undefined,
    status: c.status ?? undefined,
    lifecycleStage: c.lifecycleStage ?? undefined,
    tags: parseTags(c.tags),

    // AI state
    aiPaused: c.aiPaused ?? false,
    aiPausedAt: c.aiPausedAt ?? undefined,
    aiPausedReason: c.aiPausedReason ?? undefined,

    // CRM
    crmSource: c.crmSource ?? undefined,
    crmID: c.crmId ?? undefined,
    crmSynced: c.crmSynced ?? false,
    crmStages: c.crmStages ?? [],

    // Claimed lead — prefer claimedLead JSON string; fall back to assignedAgent typed data
    claimedLead: (() => {
      // 1. Parse claimedLead JSON string first
      if (c.claimedLead) {
        try {
          const parsed = JSON.parse(c.claimedLead);
          if (parsed.name || parsed.agentName) {
            return {
              name: parsed.name || parsed.agentName || '',
              phoneNumber: parsed.phoneNumber ?? undefined,
              id: parsed.id || parsed.teamMemberId || undefined,
              email: parsed.email ?? undefined,
              photo_url: parsed.photo_url ?? undefined,
            };
          }
        } catch {
          // fall through to assignedAgent
        }
      }
      // 2. Fall back to assignedAgent typed GQL object
      if (agent) {
        return {
          name: `${agent.firstName} ${agent.lastName}`.trim(),
          phoneNumber: agent.phone ?? undefined,
          id: agent.agentId,
          email: agent.email ?? undefined,
          photo_url: agent.photoUrl ?? undefined,
        };
      }
      return undefined;
    })(),
    hasClaimedLead: !!c.claimedLead,
    claimedLeadSource: c.claimedLeadSource ?? null,

    // Flywheel
    flywheelEnrolled: c.flywheelEnrolled ?? undefined,
    flywheelOptOut: c.flywheelOptOut ?? undefined,
    flywheelOptOutAt: c.flywheelOptOutAt ?? undefined,

    // Timestamps
    createdAt: c.createdAt ?? '',
    updatedAt: c.updatedAt ?? '',

    // Opt-in / consent
    opt_in: c.optIn ?? false,
    opt_in_date: c.optInDate ?? undefined,
    opt_in_desc: c.optInDesc ?? undefined,
    call_tester: c.callTester ?? false,
    is_test_contact: c.callTester ?? false,
    lastCallAnswered: c.lastCallAnswered ?? undefined,
    has_ealerts_registered: c.hasEalerts ?? false,
    ealerts_delivery_health: ealertStatus.deliveryHealth,
    ealerts_delivery_status: ealertStatus.deliveryStatus,
    ealerts_last_delivery_status: ealertStatus.deliveryStatus,
    ealerts_health_status: ealertStatus.deliveryHealth,
    ealerts_last_delivery_at: ealertStatus.lastDeliveryAt,
    metadata: c.metadata ?? undefined,
    customFields: c.customFields ?? undefined,
    valuationRequested: c.valuationRequested ?? false,
    websiteActivitySource: c.websiteActivitySource ?? undefined,
    contactHealth: c.contactHealth ?? undefined,
    readinessScore: c.readinessScore ?? undefined,
    readinessBand: c.readinessBand ?? undefined,

    firebaseUid: undefined,
    activeHandoffId: c.activeHandoffId ?? null,

    // Nested paginated data from GraphQL
    notes: c.notes,
    assignedAgentId: c.assignedAgentId ?? null,
    assignedPondId: c.assignedPondId ?? null,
    assignedAgent: c.assignedAgent,
    propertyEnrichment: c.propertyEnrichment ?? null,

    // Extra GraphQL fields
    outcomeHistory: c.outcomeHistory ?? null,
    crmSyncReason: c.crmSyncReason ?? null,
    crmCreated: c.crmCreated ?? null,
    crmLastUpdated: c.crmLastUpdated ?? null,
    crmSyncedAt: c.crmSyncedAt ?? null,
    lastSyncDirection: c.lastSyncDirection ?? null,
    cadencePath: c.cadencePath ?? null,
    activeFlows: c.activeFlows ?? null,
    finishedFlows: c.finishedFlows ?? null,
    activeFlowId: c.activeFlowId ?? null,
    aiAssistantId: c.aiAssistantId ?? null,
    aiThreadId: c.aiThreadId ?? null,
    lastMessageAt: c.lastMessageAt ?? null,
    lastMessageFrom: c.lastMessageFrom ?? null,
    lastSyncTimestamp: c.lastSyncTimestamp ?? null,
    lastSyncedAt: c.lastSyncedAt ?? null,

    // SPEC-1708/1709/1710/1712 — Remi intelligence fields
    suggestedStatus: c.suggestedStatus ?? null,
    consent: c.consent ?? null,
    aiIntelligence: c.aiIntelligence ?? null,
    followUpPlan: c.followUpPlan ?? null,
  };
}

// ── Hook ────────────────────────────────────────────────────────────

interface UseContactOptions {
  contactId: string | undefined;
  skip?: boolean;
}

export function useContact({ contactId, skip }: UseContactOptions) {
  const { organization } = useOrganizationStore();

  const { data, loading, error, refetch } = useApolloQuery<GetContactData>(GET_CONTACT, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId: contactId ?? '',
    },
    skip: skip || !organization?.id || !contactId,
    fetchPolicy: 'cache-first',
  });

  const contact = useMemo(() => {
    const raw = data?.contact ?? null;
    if (!raw) return null;
    const mapped = mapGQLContactDetail(raw);
    mapped.organization_id = organization?.id ?? '';
    return mapped;
  }, [data, organization?.id]);

  return {
    contact,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}
